/**
 * ゾーンシステムの計算（純関数）
 *
 * 要件定義書 §4 の式。画面のモジュールに依存しないよう exposure.js から分離した
 * （scene.js が自動保存の際に再計算するため）。
 *
 *   shootEV(ISO100) = shadow.meterEV + (5 - shadow.placedZone)
 *   fallenZone      = 5 + (spot.meterEV - shootEV)
 *   effectiveEV(EI) = shootEV + log2(EI / 100)
 *   developmentPlan: diff = round(highlight.fallenZone - highlight.placedZone)
 *                    diff > 0 なら N-diff、diff < 0 なら N+|diff|
 */

import { FILTER_FACTORS } from './presets.js';

/**
 * 2. フィルター倍数（段）。shoot.filterComp が true のときだけ露出に加える。
 * 測光をフィルターなしで行ったとき（スポットメーターなど）にオンにする。
 * 旧データ（filterComp 未設定）は 0 として扱い、これまでの計算結果を変えない。
 */
export function filterCompStops(shoot) {
  if (!shoot?.filterComp || !shoot.filterId) return 0;
  return FILTER_FACTORS[shoot.filterId]?.stops || 0;
}

export function calculateShootEV(shadowSpot) {
  if (!shadowSpot) return null;
  return shadowSpot.meterEV + (5 - shadowSpot.placedZone);
}

export function calculateFallenZone(spot, shootEV) {
  if (shootEV == null || !spot) return null;
  return 5 + (spot.meterEV - shootEV);
}

export function applyEiToEV(shootEV_ISO100, ei) {
  if (shootEV_ISO100 == null) return null;
  return shootEV_ISO100 + Math.log2((ei || 100) / 100);
}

export function calculateDevelopmentPlan(highlightSpot, shootEV) {
  if (!highlightSpot || shootEV == null) return { plan: 'N', diff: 0 };
  const fallen = calculateFallenZone(highlightSpot, shootEV);
  const target = highlightSpot.placedZone;
  const diff = Math.round(fallen - target);
  if (diff === 0) return { plan: 'N', diff };
  return { plan: diff > 0 ? `N-${diff}` : `N+${-diff}`, diff };
}

export const APERTURES = [2.8, 4, 5.6, 8, 11, 16, 22, 32, 45];
export const SHUTTERS = [
  { label: '1/1000s', sec: 1 / 1000 }, { label: '1/500s', sec: 1 / 500 },
  { label: '1/250s', sec: 1 / 250 }, { label: '1/125s', sec: 1 / 125 },
  { label: '1/60s', sec: 1 / 60 }, { label: '1/30s', sec: 1 / 30 },
  { label: '1/15s', sec: 1 / 15 }, { label: '1/8s', sec: 1 / 8 },
  { label: '1/4s', sec: 1 / 4 }, { label: '1/2s', sec: 1 / 2 },
  { label: '1s', sec: 1 }, { label: '2s', sec: 2 }, { label: '4s', sec: 4 },
  { label: '8s', sec: 8 }, { label: '15s', sec: 15 }, { label: '30s', sec: 30 },
  { label: '60s', sec: 60 },
];

/** ⅓ 段刻みの絞り（f/2.8〜f/45）。1 段の値は APERTURES と同じ */
export const APERTURES_THIRD = [2.8, 3.2, 3.5, 4, 4.5, 5, 5.6, 6.3, 7.1, 8, 9, 10, 11, 13, 14, 16, 18, 20, 22, 25, 29, 32, 36, 40, 45];
const FULL_STOP_TOLERANCE = 1 / 3; // 1 段の絞りを優先して採る範囲
const THIRD_STOP_TOLERANCE = 1 / 6; // ⅓ 段の絞りなら必ずこの範囲に入る

/** 絞りの実際の値（f/11 → 2^(7/2) など。表示値の丸めで計算がずれないように） */
function exactAperture(ap) {
  return Math.pow(2, Math.round(6 * Math.log2(ap)) / 6);
}

/**
 * EV (ISO100) から絞り・SS の組合せを提示
 *   EV = log2(N² / t)
 * シャッター速度は 1 段刻み（中判カメラの多くは 1 段）。各速度について、
 *   1. 1 段の絞り（f/8・f/11 など）で目標との差が ±1/3 段以内ならそれを使う
 *   2. なければ ⅓ 段の絞りから最も近いもの（差は必ず ±1/6 段以内）
 * 絞りは表示値ではなく実際の値（f/11 = 2^3.5 ≈ 11.31）で計算し、目標との差を返す。
 * 範囲（f/2.8〜45 × 1/1000〜60 秒）を外れる速度は出さない。
 * 以前は 1 段の値だけで差 0.18（約 0.36 段）以内を拾っていたため、
 * EV によっては候補が 0 件になったり、偏った組合せだけが残ったりした。
 *
 * @returns {Array<{ aperture, shutter, evCheck, diffStops, primary }>}
 *   diffStops：組合せの EV − 目標の EV（段）。＋なら目標より露光が少なく、−なら多い
 */
/**
 * 4. カメラ・レンズの範囲。{ fastest, slowest, widest, smallest }（秒・f 値）。
 * 未設定の項目は全範囲（1/1000〜60 秒、f/2.8〜45）。
 */
export function rangeOf(src) {
  const r = src?.expoRange || {};
  return {
    fastest: Number(r.fastest) > 0 ? Number(r.fastest) : 1 / 1000,
    slowest: Number(r.slowest) > 0 ? Number(r.slowest) : 60,
    widest: Number(r.widest) > 0 ? Number(r.widest) : 2.8,
    smallest: Number(r.smallest) > 0 ? Number(r.smallest) : 45,
  };
}

export function generateExposureCombinations(ev_ISO100, ei, range = null) {
  if (ev_ISO100 == null) return [];
  const evEi = applyEiToEV(ev_ISO100, ei);
  const R = rangeOf({ expoRange: range });
  const inAp = (ap) => ap >= R.widest - 0.05 && ap <= R.smallest + 0.05;
  const fullAps = APERTURES.filter(inAp);
  const thirdAps = APERTURES_THIRD.filter(inAp);
  const matches = [];
  SHUTTERS.filter(({ sec }) => sec >= R.fastest * 0.99 && sec <= R.slowest * 1.01).forEach(({ label, sec }) => {
    const requiredN = Math.sqrt(sec * Math.pow(2, evEi));
    const pick = (list) => {
      let best = null;
      list.forEach((ap) => {
        const d = 2 * Math.log2(exactAperture(ap) / requiredN); // 段
        if (!best || Math.abs(d) < Math.abs(best.d)) best = { ap, d };
      });
      return best;
    };
    let best = pick(fullAps);
    if (!best || Math.abs(best.d) > FULL_STOP_TOLERANCE + 1e-9) {
      best = pick(thirdAps);
      if (!best || Math.abs(best.d) > THIRD_STOP_TOLERANCE + 1e-9) return; // 範囲外
    }
    const evCheck = Math.log2((exactAperture(best.ap) ** 2) / sec);
    matches.push({
      aperture: `f/${best.ap}`,
      shutter: label,
      evCheck,
      diffStops: Math.round((evCheck - evEi) * 100) / 100,
      primary: false,
    });
  });
  // 代表は f/11・16・8・22・5.6 × 1/30・1/60・1/15・1/125・1/8 の優先順（要件定義書 §4）
  const preferredApertures = [11, 16, 8, 22, 5.6];
  const preferredShutters = ['1/30s', '1/60s', '1/15s', '1/125s', '1/8s'];
  for (const ap of preferredApertures) {
    for (const sh of preferredShutters) {
      // 目標との差が ¼ 段を超えるものは代表にしない（もっと近い組合せがあるため）
      const found = matches.find((m) => m.aperture === `f/${ap}` && m.shutter === sh && Math.abs(m.diffStops) <= 0.25);
      if (found) { found.primary = true; return matches; }
    }
  }
  // 1 段の絞りで見つからないときは、好みの速度の中で f/11 に最も近い絞り
  const near = matches
    .filter((m) => preferredShutters.includes(m.shutter))
    .sort((x, y) => Math.abs(Math.log2(parseFloat(x.aperture.slice(2)) / 11)) - Math.abs(Math.log2(parseFloat(y.aperture.slice(2)) / 11)))[0];
  if (near) near.primary = true;
  else if (matches.length > 0) matches[0].primary = true;
  return matches;
}

export function computeShootResult(shoot) {
  if (!shoot) return null;
  const spots = shoot.spots || [];
  const shadow = spots.find((s) => s.role === 'shadow-anchor');
  const highlight = spots.find((s) => s.role === 'highlight-anchor');

  const shootEV = calculateShootEV(shadow);
  const planResult = calculateDevelopmentPlan(highlight, shootEV);

  const spotsWithFallen = spots.map((s) => ({
    ...s,
    fallenZone: calculateFallenZone(s, shootEV),
  }));

  // フィルター倍数は露出を増やす向き（EV を下げる）。ゾーンの落ち先・現像方針には影響しない
  const filterStops = filterCompStops(shoot);
  const combos = generateExposureCombinations(shootEV == null ? null : shootEV - filterStops, shoot.ei, shoot.expoRange || null);
  const primary = combos.find((c) => c.primary) || combos[0];

  let dynamicRange = null;
  if (shadow && highlight) dynamicRange = highlight.meterEV - shadow.meterEV;

  return {
    shootEV,
    plan: planResult.plan,
    planDiff: planResult.diff,
    spotsWithFallen,
    combos,
    primary,
    dynamicRange,
    shadow,
    highlight,
    filterStops,
  };
}

/**
 * シーンに書き戻す計算結果（露出画面の保存で書いていた項目と同じ）
 * 主要シャドウが無いときは現像方針を null にする。
 */
export function derivedShootFields(shoot) {
  const r = computeShootResult(shoot);
  if (!r) return {};
  return {
    baseShootEV: r.shootEV,
    aperture: r.primary?.aperture ?? null,
    shutterSpeed: r.primary?.shutter ?? null,
    shadowAnchorId: r.shadow?.id ?? null,
    highlightAnchorId: r.highlight?.id ?? null,
    developmentPlan: r.shootEV == null ? null : r.plan,
  };
}

/** 表示用：保存値 'N-1' を 'N−1'（数学の負号）にする */
export function planLabel(plan) {
  if (!plan) return '—';
  return String(plan).replace('-', '−');
}
